import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { OfflineDBManager } from './offline-db-manager';
import { SyncQueueManager, SyncProgress, SyncResult } from './sync-queue-manager';
import { ConflictResolver } from './conflict-resolver';

export interface OfflineState {
  // Connection status
  isOnline: boolean;
  lastOnlineTime: string | null;
  connectionQuality: 'good' | 'poor' | 'offline';
  
  // Sync status
  syncInProgress: boolean;
  syncProgress: SyncProgress | null;
  lastSyncTime: string | null;
  pendingSyncCount: number;
  conflictCount: number;
  
  // Cache status
  cacheSize: number;
  cacheLastUpdated: string | null;
  
  // PWA status
  updateAvailable: boolean;
  
  // Feature flags
  offlineModeEnabled: boolean;
  backgroundSyncEnabled: boolean;
  notificationsEnabled: boolean;
}

export interface OfflineActions {
  // Connection management
  setOnlineStatus: (isOnline: boolean) => void;
  updateConnectionQuality: (quality: 'good' | 'poor' | 'offline') => void;
  
  // Sync management
  startSync: () => Promise<SyncResult>;
  pauseSync: () => void;
  resumeSync: () => void;
  updateSyncProgress: (progress: SyncProgress) => void;
  
  // Cache management
  updateCacheSize: () => Promise<void>;
  clearOldCache: (maxAge: number) => Promise<number>;
  
  // PWA management
  checkForUpdates: () => Promise<void>;
  
  // Settings
  toggleOfflineMode: () => void;
  toggleBackgroundSync: () => void;
  toggleNotifications: () => void;
  
  // Initialization
  initialize: () => Promise<void>;
  destroy: () => void;
}

export interface OfflineStore extends OfflineState, OfflineActions {}

class OfflineManager {
  private dbManager: OfflineDBManager;
  private syncManager: SyncQueueManager;
  private conflictResolver: ConflictResolver;
  private swRegistration: ServiceWorkerRegistration | null = null;
  private connectionCheckInterval: number | null = null;
  
  constructor() {
    this.dbManager = new OfflineDBManager();
    this.syncManager = new SyncQueueManager(this.dbManager);
    this.conflictResolver = new ConflictResolver(this.dbManager);
  }
  
  async initialize(store: OfflineStore): Promise<void> {
    // Initialize IndexedDB
    await this.dbManager.initialize();
    
    // Set up event listeners
    this.setupEventListeners(store);
    
    // Register service worker
    await this.registerServiceWorker(store);
    
    // Check initial status
    await this.checkInitialStatus(store);
    
    // Start connection monitoring
    this.startConnectionMonitoring(store);
    
    // Set up sync event listeners
    this.setupSyncListeners(store);
  }
  
  private setupEventListeners(store: OfflineStore): void {
    // Online/offline events
    window.addEventListener('online', () => {
      console.log('[OfflineManager] Connection restored');
      store.setOnlineStatus(true);
      
      if (store.backgroundSyncEnabled) {
        store.startSync();
      }
    });
    
    window.addEventListener('offline', () => {
      console.log('[OfflineManager] Connection lost');
      store.setOnlineStatus(false);
    });
    
  }
  
  private async registerServiceWorker(store: OfflineStore): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        this.swRegistration = registration;
        
        console.log('[OfflineManager] Service Worker registered');
        
        // Check for updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          newWorker?.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              store.updateAvailable = true;
              console.log('[OfflineManager] New update available');
            }
          });
        });
        
        // Handle messages from service worker
        navigator.serviceWorker.addEventListener('message', (event) => {
          this.handleServiceWorkerMessage(event, store);
        });
      } catch (error) {
        console.error('[OfflineManager] Service Worker registration failed:', error);
      }
    }
  }
  
  private handleServiceWorkerMessage(event: MessageEvent, store: OfflineStore): void {
    const { type, data } = event.data;
    
    switch (type) {
      case 'SYNC_PROGRESS':
        store.updateSyncProgress(data);
        break;
      case 'SYNC_COMPLETE':
        store.lastSyncTime = new Date().toISOString();
        store.syncInProgress = false;
        break;
      case 'CACHE_UPDATED':
        store.updateCacheSize();
        break;
      case 'CONFLICT_DETECTED':
        store.conflictCount++;
        break;
    }
  }
  
  private async checkInitialStatus(store: OfflineStore): Promise<void> {
    // Check online status
    store.setOnlineStatus(navigator.onLine);
    
    // Get pending sync count
    store.pendingSyncCount = await this.dbManager.getPendingSyncOperationsCount();
    
    // Get conflict count
    store.conflictCount = await this.dbManager.getConflictsCount();
    
    // Update cache size
    await store.updateCacheSize();
  }
  
  private startConnectionMonitoring(store: OfflineStore): void {
    // Monitor connection quality
    this.connectionCheckInterval = window.setInterval(() => {
      if (navigator.onLine) {
        this.checkConnectionQuality(store);
      }
    }, 30000); // Every 30 seconds
  }
  
  private async checkConnectionQuality(store: OfflineStore): Promise<void> {
    try {
      const start = performance.now();
      const response = await fetch('/api/health', {
        method: 'HEAD',
        cache: 'no-cache',
      });
      const end = performance.now();
      const latency = end - start;
      
      if (response.ok && latency < 200) {
        store.updateConnectionQuality('good');
      } else if (response.ok) {
        store.updateConnectionQuality('poor');
      }
    } catch (error) {
      store.updateConnectionQuality('poor');
    }
  }
  
  private setupSyncListeners(store: OfflineStore): void {
    // Listen to sync queue events
    this.syncManager.on('sync-started', ({ total }) => {
      store.syncInProgress = true;
      store.updateSyncProgress({
        total,
        current: 0,
        status: 'syncing',
      });
    });
    
    this.syncManager.on('sync-progress', (progress: SyncProgress) => {
      store.updateSyncProgress(progress);
    });
    
    this.syncManager.on('sync-completed', (result: SyncResult) => {
      store.syncInProgress = false;
      store.lastSyncTime = new Date().toISOString();
      store.pendingSyncCount = 0;
      store.updateSyncProgress({
        total: result.processed,
        current: result.processed,
        status: 'idle',
      });
    });
    
    this.syncManager.on('conflict-detected', () => {
      store.conflictCount++;
    });
    
    this.syncManager.on('conflict-auto-resolved', () => {
      store.conflictCount = Math.max(0, store.conflictCount - 1);
    });
  }
  
  async requestBackgroundSync(tag: string): Promise<void> {
    if ('sync' in this.swRegistration!) {
      try {
        await (this.swRegistration as any).sync.register(tag);
        console.log(`[OfflineManager] Background sync registered: ${tag}`);
      } catch (error) {
        console.error('[OfflineManager] Background sync registration failed:', error);
      }
    }
  }
  
  async enableNotifications(): Promise<boolean> {
    if ('Notification' in window && this.swRegistration) {
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        // Subscribe to push notifications
        try {
          const subscription = await this.swRegistration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: this.urlBase64ToUint8Array(
              import.meta.env.VITE_VAPID_PUBLIC_KEY || ''
            ),
          });
          
          // Send subscription to server
          await this.sendSubscriptionToServer(subscription);
          
          return true;
        } catch (error) {
          console.error('[OfflineManager] Push subscription failed:', error);
          return false;
        }
      }
    }
    
    return false;
  }
  
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    
    return outputArray;
  }
  
  private async sendSubscriptionToServer(subscription: PushSubscription): Promise<void> {
    // Implementation would send to your notification service
    console.log('[OfflineManager] Push subscription:', subscription);
  }
  
  destroy(): void {
    if (this.connectionCheckInterval) {
      clearInterval(this.connectionCheckInterval);
    }
    
    this.syncManager.destroy();
    this.dbManager.close();
  }
  
  // Getters for store actions
  getDbManager(): OfflineDBManager {
    return this.dbManager;
  }
  
  getSyncManager(): SyncQueueManager {
    return this.syncManager;
  }
  
  getConflictResolver(): ConflictResolver {
    return this.conflictResolver;
  }
  
  getSwRegistration(): ServiceWorkerRegistration | null {
    return this.swRegistration;
  }
}

// Create singleton instance
const offlineManager = new OfflineManager();

// Create Zustand store
export const useOfflineStore = create<OfflineStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        isOnline: navigator.onLine,
        lastOnlineTime: null,
        connectionQuality: navigator.onLine ? 'good' : 'offline',
        syncInProgress: false,
        syncProgress: null,
        lastSyncTime: null,
        pendingSyncCount: 0,
        conflictCount: 0,
        cacheSize: 0,
        cacheLastUpdated: null,
        updateAvailable: false,
        offlineModeEnabled: true,
        backgroundSyncEnabled: true,
        notificationsEnabled: false,
        
        // Actions
        setOnlineStatus: (isOnline) => set({
          isOnline,
          lastOnlineTime: isOnline ? new Date().toISOString() : get().lastOnlineTime,
          connectionQuality: isOnline ? 'good' : 'offline',
        }),
        
        updateConnectionQuality: (quality) => set({ connectionQuality: quality }),
        
        startSync: async () => {
          const syncManager = offlineManager.getSyncManager();
          set({ syncInProgress: true });
          
          try {
            const result = await syncManager.processSyncQueue();
            set({
              pendingSyncCount: await offlineManager.getDbManager().getPendingSyncOperationsCount(),
              conflictCount: await offlineManager.getDbManager().getConflictsCount(),
            });
            return result;
          } catch (error) {
            console.error('[OfflineStore] Sync failed:', error);
            throw error;
          } finally {
            set({ syncInProgress: false });
          }
        },
        
        pauseSync: () => {
          offlineManager.getSyncManager().pauseSync();
          set({ syncInProgress: false });
        },
        
        resumeSync: () => {
          offlineManager.getSyncManager().resumeSync();
        },
        
        updateSyncProgress: (progress) => set({ syncProgress: progress }),
        
        updateCacheSize: async () => {
          const size = await offlineManager.getDbManager().getCacheSize();
          set({
            cacheSize: size,
            cacheLastUpdated: new Date().toISOString(),
          });
        },
        
        clearOldCache: async (maxAge) => {
          const deletedCount = await offlineManager.getDbManager().clearOldCache(maxAge);
          await get().updateCacheSize();
          return deletedCount;
        },
        
        checkForUpdates: async () => {
          const registration = offlineManager.getSwRegistration();
          if (registration) {
            await registration.update();
          }
        },
        
        toggleOfflineMode: () => set((state) => ({
          offlineModeEnabled: !state.offlineModeEnabled,
        })),
        
        toggleBackgroundSync: () => set((state) => ({
          backgroundSyncEnabled: !state.backgroundSyncEnabled,
        })),
        
        toggleNotifications: async () => {
          const enabled = await offlineManager.enableNotifications();
          set({ notificationsEnabled: enabled });
        },
        
        initialize: async () => {
          await offlineManager.initialize(get());
        },
        
        destroy: () => {
          offlineManager.destroy();
        },
      }),
      {
        name: 'offline-store',
        partialize: (state) => ({
          offlineModeEnabled: state.offlineModeEnabled,
          backgroundSyncEnabled: state.backgroundSyncEnabled,
          notificationsEnabled: state.notificationsEnabled,
        }),
      }
    )
  )
);